import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';

function VectorChat({ token, chatId, onChatIdChange, environment }) {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [externalApplication, setExternalApplication] = useState('');
  const [externalId, setExternalId] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        let url = `/api/vector-chat/history?chatId=${chatId}`;
        if (externalApplication) url += `&externalApplication=${encodeURIComponent(externalApplication)}`;
        if (externalId) url += `&externalId=${encodeURIComponent(externalId)}`;

        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const history = await response.json();
          setMessages(history);
        }
      } catch (error) {
        console.error('Error fetching chat history:', error);
      }
    };

    const fetchDocuments = async () => {
      try {
        let url = `/api/vector-chat/documents?chatId=${chatId}`;
        if (externalApplication) url += `&externalApplication=${encodeURIComponent(externalApplication)}`;
        if (externalId) url += `&externalId=${encodeURIComponent(externalId)}`;

        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const documents = await response.json();
          setUploadedFiles(documents);
        }
      } catch (error) {
        console.error('Error fetching documents:', error);
      }
    };

    fetchHistory();
    fetchDocuments();
  }, [chatId, token, externalApplication, externalId]);

  const handleNewChat = () => {
    // Clear messages and uploaded files for the new session immediately
    setMessages([]);
    setUploadedFiles([]);
    
    const newId = 'vec_' + Math.random().toString(36).substring(7);
    
    // Add the new session to the top of the list immediately
    setSessions(prev => {
      if (prev.includes(newId)) return prev;
      return [newId, ...prev];
    });
    
    // Update the chatId in the parent component
    onChatIdChange(newId);
  };

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        let url = '/api/vector-chat/sessions';
        const params = new URLSearchParams();
        if (externalApplication) params.append('externalApplication', externalApplication);
        if (externalId) params.append('externalId', externalId);
        if (params.toString()) url += `?${params.toString()}`;

        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const sessionList = await response.json();
          // Merge local sessions (like a newly created one) with those from the server
          setSessions(prev => {
            const merged = [...new Set([...prev, ...sessionList])];
            // If the current chatId is not in the list (e.g., brand new session), add it
            if (chatId && chatId.startsWith('vec_') && !merged.includes(chatId)) {
              return [chatId, ...merged];
            }
            return merged;
          });
        }
      } catch (error) {
        console.error('Error fetching sessions:', error);
      }
    };

    fetchSessions();
  }, [token, chatId, externalApplication, externalId]); // Fetch sessions on mount, when token changes, or when chatId changes (to include brand new sessions)


  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleUpload = async () => {
    if (!file) return;
    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('chatId', chatId);
    if (externalApplication) formData.append('externalApplication', externalApplication);
    if (externalId) formData.append('externalId', externalId);

    try {
      const response = await fetch('/api/vector-chat/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        setUploadedFiles(prev => [...prev, file.name]);
        setFile(null);
        document.getElementById('vectorFileInput').value = '';
      } else {
        alert('Failed to upload document');
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      alert('Error uploading document');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    const aiMessage = { role: 'assistant', content: '' };
    setMessages(prev => [...prev, aiMessage]);

    try {
      let url = `/api/vector-chat/stream?message=${encodeURIComponent(input)}&chatId=${chatId}`;
      if (externalApplication) url += `&externalApplication=${encodeURIComponent(externalApplication)}`;
      if (externalId) url += `&externalId=${encodeURIComponent(externalId)}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Chat request failed with status ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let done = false;
      let buffer = '';
      let fullContent = '';

      while (!done) {
        const { value, done: doneReading } = await reader.read();
        done = doneReading;
        buffer += decoder.decode(value, { stream: !done });
        
        let lines = buffer.split('\n');
        buffer = lines.pop();
        
        let extractedContent = '';
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          if (line.startsWith('data:')) {
            extractedContent += line.substring(5);
            if (i + 1 < lines.length && lines[i + 1].startsWith('data:')) {
              extractedContent += '\n';
            }
          }
        }
        
        if (extractedContent) {
          fullContent += extractedContent;
          const contentSnapshot = fullContent;
          setMessages(prev => {
            const lastMessage = prev[prev.length - 1];
            const updatedLastMessage = { ...lastMessage, content: contentSnapshot };
            return [...prev.slice(0, -1), updatedLastMessage];
          });
        }
      }
    } catch (error) {
      console.error('Error in chat:', error);
      setMessages(prev => {
        const newMessages = [...prev];
        if (newMessages.length > 0 && newMessages[newMessages.length - 1].role === 'assistant' && newMessages[newMessages.length - 1].content === '') {
          newMessages.pop();
        }
        return [...newMessages, { role: 'error', content: error.message || 'Failed to get response from AI.' }];
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mt-4 row">
      <div className="col-md-2">
        <div className="card shadow-sm mb-4">
          <div className="card-header bg-dark text-white d-flex justify-content-between align-items-center">
            <h6 className="mb-0">Chat Sessions</h6>
            <button className="btn btn-sm btn-outline-light" onClick={handleNewChat}>+</button>
          </div>
          <div className="list-group list-group-flush" style={{ maxHeight: '600px', overflowY: 'auto' }}>
            {sessions.length === 0 && <div className="list-group-item small text-muted">No sessions yet</div>}
            {sessions.map((sid) => (
              <button
                key={sid}
                onClick={() => onChatIdChange(sid)}
                className={`list-group-item list-group-item-action small ${sid === chatId ? 'active' : ''}`}
                style={{ wordBreak: 'break-all' }}
              >
                {sid}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="col-md-10">
        <div className="card shadow-sm mb-4">
          <div className="card-header bg-success text-white">
            <h5 className="mb-0">Vector Store RAG (Token Efficient)</h5>
          </div>
          <div className="card-body">
            <div className="mb-3">
              <p className="mb-0 text-muted small">
                This mode uses Vector Search to only send relevant parts of your documents to the AI, saving tokens and reducing cost.
              </p>
              <p className="mb-0 small">
                Current Chat ID: <strong>{chatId}</strong>
              </p>
            </div>
            
            <div className="row mb-3">
              <div className="col-md-6">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="External Application (optional)"
                  value={externalApplication}
                  onChange={(e) => setExternalApplication(e.target.value)}
                  maxLength="50"
                />
              </div>
              <div className="col-md-6">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="External ID (optional)"
                  value={externalId}
                  onChange={(e) => setExternalId(e.target.value)}
                  maxLength="100"
                />
              </div>
            </div>

            <div className="input-group mb-3">
              <input
                type="file"
                className="form-control"
                id="vectorFileInput"
                accept=".pdf"
                onChange={handleFileChange}
                disabled={isUploading}
              />
              <button
                className="btn btn-success"
                type="button"
                onClick={handleUpload}
                disabled={!file || isUploading}
              >
                {isUploading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Indexing...
                  </>
                ) : 'Index PDF'}
              </button>
            </div>
            {uploadedFiles.length > 0 && (
              <div className="mb-3">
                <span className="small text-muted d-block mb-1">Attached Documents:</span>
                {uploadedFiles.map((name, idx) => (
                  <span key={idx} className="badge bg-success me-1" title={name}>
                    {name}
                  </span>
                ))}
              </div>
            )}
            {environment === 'development' && (
              <div className="alert alert-warning py-1 px-2 mt-2 mb-0 small" role="alert">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                <strong>Note:</strong> Documents are stored in-memory in dev and will be cleared after restart.
              </div>
            )}
          </div>
        </div>

        <div className="card shadow-sm">
          <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Vector Chat</h5>
          </div>
          <div className="card-body" style={{ height: '600px', overflowY: 'auto' }}>
            {messages.length === 0 && <div className="text-center text-muted mt-5">Start a conversation or upload a document!</div>}
            {messages.map((msg, index) => (
              <div key={index} className={`mb-2 d-flex ${msg.role === 'user' ? 'justify-content-end' : 'justify-content-start text-start'}`}>
                <div className={`p-3 rounded ${msg.role === 'user' ? 'bg-primary text-white' : (msg.role === 'error' ? 'bg-danger text-white' : 'bg-light text-dark')}`} style={{ maxWidth: '80%', whiteSpace: msg.role === 'user' ? 'pre-wrap' : 'normal', textAlign: 'left' }}>
                  {msg.role === 'assistant' ? (
                    <div className="markdown-container">
                      <ReactMarkdown
                        components={{
                          p: ({ node, ...props }) => <p className="mb-2" {...props} />,
                          ul: ({ node, ...props }) => <ul className="mb-2 ps-3" {...props} />,
                          ol: ({ node, ...props }) => <ol className="mb-2 ps-3" {...props} />,
                          li: ({ node, ...props }) => <li className="mb-0" {...props} />,
                          h1: ({ node, children, ...props }) => <h1 className="h6 mb-2 fw-bold" {...props}>{children}</h1>,
                          h2: ({ node, children, ...props }) => <h2 className="h6 mb-2 fw-bold" {...props}>{children}</h2>,
                          h3: ({ node, children, ...props }) => <h3 className="h6 mb-2 fw-bold" {...props}>{children}</h3>,
                          h4: ({ node, children, ...props }) => <h4 className="h6 mb-2 fw-bold" {...props}>{children}</h4>,
                          h5: ({ node, children, ...props }) => <h5 className="h6 mb-2 fw-bold" {...props}>{children}</h5>,
                          h6: ({ node, children, ...props }) => <h6 className="h6 mb-2 fw-bold" {...props}>{children}</h6>
                        }}
                      >
                        {msg.content}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    msg.content
                  )}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
          <div className="card-footer">
            <form onSubmit={handleSubmit} className="d-flex">
              <input
                type="text"
                className="form-control me-2"
                placeholder="Search vector store and chat..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                disabled={isLoading}
              />
              <button type="submit" className="btn btn-primary" disabled={isLoading}>
                {isLoading ? '...' : 'Send'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VectorChat;
